import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLocale } from '@shared/lib/i18n/i18n';
import { writeDeviceLocale, type Locale } from '@shared/lib/i18n/locales';
import { sessionStore } from '@shared/session/store';
import { describeError } from '@shared/ui/FormError';
import { toast } from '@shared/ui/Toast';
import { authApi } from '../api';

/**
 * Switch the UI language: swap lang/dir immediately, remember it on this device, and persist
 * users.preferred_language when signed in (i18n doc §13.2).
 */
export function useChangeLocale(): (locale: Locale) => Promise<void> {
  const { t } = useTranslation('common');
  return useCallback(
    async (locale: Locale) => {
      await changeLocale(locale);
      writeDeviceLocale(locale);
      const state = sessionStore.get();
      if (state.status !== 'authenticated') return;
      try {
        const me = await authApi.updateMe({ preferred_language: locale });
        state.applyMe(me);
      } catch (error) {
        const { title } = describeError(error, t);
        toast({ tone: 'warning', title: t('language.saveFailed'), description: title });
      }
    },
    [t],
  );
}
